import React, { useState, useRef, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Spinner,
} from 'reactstrap';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Avatar from '../avatar/avatar';
import qapi from '../views_shared_copies/api';
import vahoy from '../../javascript/vahoy';

// Phase B peer-DM compose modal (a deliberately disposable V1-compat shim -- it dies with V1).
//
// It is the front door to the V2 inbox: the user types one message and is dropped into the V2
// thread. All V1->V2 mapping lives here, keeping the V2 Interact API permanently clean:
//   1. resolve the peer's V1 user id -> V2 profile (profile_id + granular type_of_profile)
//   2. map type_of_profile -> conversation origin STI type
//   3. create the conversation against V2 (the server enforces 1:1 + same-cohort and reuses an
//      existing pair thread), then deep-link to /my_inbox_v2?conversation=<uuid>.
//
// Map the peer's granular type_of_profile to its conversation origin STI class. Any promoter subtype
// (PR, self-expert, or hybrid) is a Promoter origin; media is a MediaProfile origin. The server gates
// who may message whom (promoter<->promoter or media<->media); this map is just for the payload.
const ORIGIN_TYPE_BY_PROFILE_TYPE = {
  type_media: 'Id::Profile::MediaProfile',
  type_promoter_pr: 'Id::Profile::Promoter',
  type_promoter_expert: 'Id::Profile::Promoter',
  type_promoter_hybrid: 'Id::Profile::Promoter',
};

const GENERIC_ERROR = "Couldn't send — please try again.";

// DM body posts to the V2 Interact API, which caps Interact::Message at 5000 chars.
const MAX_MESSAGE_LENGTH = 5000;
// Keep the counter hidden for short messages; reveal it once it's long enough to matter.
const CHAR_COUNTER_REVEAL_AT = 1000;

function PeerDmModal({
  isOpen, onClose, peerUserId, peerFirstName, peerName, peerTitle, peerAvatarUrl, peerInitials,
}) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) textareaRef.current.focus();
  }, [isOpen]);

  const close = () => {
    if (sending) return;
    if (body.trim() && !window.confirm('Discard your message?')) return;
    setBody('');
    setError(null);
    onClose();
  };

  const send = async () => {
    if (!body.trim() || sending || body.length > MAX_MESSAGE_LENGTH) return;

    setSending(true);
    setError(null);
    vahoy.track('PeerDmModal#send');

    try {
      const { data: profileData, error: lookupError } = await qapi.userProfiles.getByUserMigrateId(peerUserId);
      const profile = Array.isArray(profileData) ? profileData[0] : null;
      const originType = profile && ORIGIN_TYPE_BY_PROFILE_TYPE[profile.type_of_profile];

      if (lookupError || !profile || !originType) {
        window.Rollbar?.error('PeerDmModal: could not resolve peer to a V2 profile/origin', {
          peerUserId, lookupError, typeOfProfile: profile?.type_of_profile,
        });
        setError(GENERIC_ERROR);
        setSending(false);
        return;
      }

      const { data: result, error: createError } = await qapi.conversations.create({
        body,
        originType,
        originId: profile.profile_id,
        receiverIds: [profile.profile_id],
      });

      const uuid = result?.data?.attributes?.conversation_id;
      if (createError || !uuid) {
        window.Rollbar?.error('PeerDmModal: V2 conversation create failed', {
          peerUserId, originType, createError,
        });
        setError(GENERIC_ERROR);
        setSending(false);
        return;
      }

      // Land the user in the V2 inbox on the new (or reused) thread. Full-page nav -- the inbox's
      // useDeepLinkResolver opens the conversation from the ?conversation=<uuid> param.
      window.location.href = `/my_inbox_v2?conversation=${uuid}`;
    } catch (e) {
      window.Rollbar?.error('PeerDmModal: unexpected error sending peer DM', e);
      setError(GENERIC_ERROR);
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    // Cmd/Ctrl+Enter sends; plain Enter inserts a newline, so a half-written message can't fire.
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      send();
    }
  };

  const charCount = body.length;
  const overLimit = charCount > MAX_MESSAGE_LENGTH;

  const closeBtn = (
    <button type="button" className="btn-close" aria-label="Close" onClick={close} />
  );

  return (
    <Modal isOpen={isOpen} toggle={close} centered className="peer-dm-modal">
      <ModalHeader toggle={close} close={closeBtn} className="border-0 p-4 pb-0 align-items-start">
        <div className="d-flex align-items-center">
          <Avatar avatarUrl={peerAvatarUrl} avatarLetters={peerInitials} sizeClass="avatar-sm" />
          <div>
            <div className="fw-bold">{peerName}</div>
            {peerTitle && <div className="text-muted font-size-12px">{peerTitle}</div>}
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="px-4">
        <textarea
          ref={textareaRef}
          name="peer_dm_body"
          aria-label="Message body"
          className={classnames('form-control shadow-none', { 'is-invalid': overLimit })}
          placeholder={`Message ${peerFirstName}`}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={handleKeyDown}
          rows="6"
          disabled={sending}
        />
        {charCount >= CHAR_COUNTER_REVEAL_AT && (
          <div className="d-flex justify-content-end mt-1">
            <span
              className={classnames('small', overLimit ? 'text-danger' : 'text-muted')}
              data-testid="char-count"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {charCount}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
        )}
        {error && <div className="text-danger mt-2 font-size-12px">{error}</div>}
      </ModalBody>

      <ModalFooter className="border-0 p-4 pt-2">
        <button type="button" className="btn btn-secondary btn-sm" onClick={close} disabled={sending}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={send}
          disabled={!body.trim() || sending || overLimit}
        >
          {sending ? (
            <>
              <Spinner size="sm" />
              <span className="ms-1">Sending…</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane me-1" />
              Send
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}

PeerDmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  peerUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  peerFirstName: PropTypes.string,
  peerName: PropTypes.string,
  peerTitle: PropTypes.string,
  peerAvatarUrl: PropTypes.string,
  peerInitials: PropTypes.string,
};

PeerDmModal.defaultProps = {
  peerFirstName: 'them',
  peerName: undefined,
  peerTitle: undefined,
  peerAvatarUrl: undefined,
  peerInitials: undefined,
};

export default PeerDmModal;
